import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import { Api, useApiFormSubmit } from '../../api';
import { Model } from '../../models';
import SoloLoading from '../SoloLoading';
import { useForm, FieldProp } from 'react-typed-form';

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string;
  existingSymptoms: Api.UserStats.V['user']['frequentSymptoms'];
};

export default function EditFrequentSymptomsModal({
  open,
  onClose,
  userId,
  existingSymptoms,
}: Props) {
  const { invalidateExact } = useInvalidation();
  const symptomsApi = Api.Symptom.useReadCollection();

  const form = useForm<{ frequentSymptoms: string[] }>({
    defaultValues: {
      frequentSymptoms: existingSymptoms.map((s) => s['@id']),
    },
    onSubmit: useApiFormSubmit<{ frequentSymptoms: string[] }>(
      'PATCH',
      `/users/${userId}/as-admin`,
      {
        onSuccess: () => {
          invalidateExact(`/users/${userId}/stats`);
          toast.success('Frequent symptoms updated successfully');
          onClose();
        },
      },
    ),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Edit Frequent Symptoms</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <ModalLoaded
        api={symptomsApi}
        field={form.getField('frequentSymptoms')}
      />

      <DialogActions>
        <Button onClick={form.handleSubmit} variant="contained">
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ModalLoaded({
  api,
  field,
}: {
  api: ReturnType<typeof Api.Symptom.useReadCollection>;
  field: FieldProp<string[]>;
}) {
  if (api.error) {
    return <Typography color="error">Error loading symptoms</Typography>;
  }

  if (!api.data) {
    return <SoloLoading />;
  }

  const symptoms = api.data.member;
  const symptomsByCategory = Model.Symptom.groupSymptomsByCategory(symptoms);

  const selectedSymptoms = field.value || [];

  const handleSymptomChange = (symptomId: string, checked: boolean) => {
    if (checked) {
      field.handleValueChange([...selectedSymptoms, symptomId]);
    } else {
      field.handleValueChange(
        selectedSymptoms.filter((id) => id !== symptomId),
      );
    }
  };

  return (
    <DialogContent>
      <Stack spacing={3} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Users have the ability to overwrite your changes in the app
        </Typography>

        <Box
          sx={{
            bgcolor: 'var(--mui-palette-background-level1)',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="subtitle1">
              Do you experience any of the following symptoms?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select all that apply
            </Typography>

            <Stack spacing={2}>
              {Object.entries(symptomsByCategory).map(
                ([categoryName, categorySymptoms]) => (
                  <Stack key={categoryName} spacing={1}>
                    <Typography variant="subtitle2">{categoryName}</Typography>
                    {categorySymptoms.map((symptom) => (
                      <FormControlLabel
                        key={symptom['@id']}
                        control={
                          <Checkbox
                            checked={selectedSymptoms.includes(symptom['@id'])}
                            onChange={(e) =>
                              handleSymptomChange(
                                symptom['@id'],
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={symptom.name}
                      />
                    ))}
                  </Stack>
                ),
              )}
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </DialogContent>
  );
}
